import React, { useState, useEffect, useContext, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import UploadButtonMessage from '../UploadB/UploadButtonMessage';
import N8nConfirmationCard from '../N8nConfirmationCard';
import SkillResultCard from '../SkillResultCard';
import ActivityStepInline from '../ActivityStepInline';
import ActivityStepSummary from '../ActivityStepInline/ActivityStepSummary';
import { AuthContext } from '../../authContext';
import { useTranslation } from 'react-i18next';
import { translateBackendError } from '../../utils/translateBackendError';
import { translateInlineText } from '../../utils/translateInlineText';
import { removerMarcadoresInternos, normalizarSeparadorMilhar } from '../../utils/mensagemVisivel';
import './Message.css';

function normalizeFilename(filename) {
  return filename
    .toLowerCase()
    .replace(/\s+\./g, '.')
    .replace(/[^\w]/g, '');
}

// Substitui o nome de arquivo referenciado pelo assistente por um link markdown clicável,
// usando o UUID mencionado na mesma entrada como fallback quando o filename não casa exatamente.
function linkifyReferences(content, references = []) {
  if (typeof content !== 'string' || !content || !references.length) {
    return content;
  }

  const linkByUuid = {};
  const linkByFilename = {};

  references.forEach(({ uuid, filename, link }) => {
    if (!link) return;
    if (uuid) linkByUuid[uuid.toLowerCase()] = link;
    if (filename) linkByFilename[normalizeFilename(filename)] = link;
  });

  return content.replace(
    /((?:\*\*)?File Name(?:\*\*)?:\s*\[?)([^\n\]]+?\.(?:docx?|pdf))(\]?)([\s\S]*?)((?:\*\*)?UUID(?:\*\*)?:\s*)([a-f0-9-]{36})/gi,
    (match, prefix, rawFilename, closeBracket, between, uuidLabel, uuid) => {
      const link = linkByFilename[normalizeFilename(rawFilename)] || linkByUuid[uuid.toLowerCase()];
      if (!link) return match;
      return `${prefix.replace('[', '')}[${rawFilename}](${link})${between}${uuidLabel}${uuid}`;
    }
  );
}

function Message({ msg, index, isReportBox = false, sessionId, isSending, assistantId, links, isActivityHistoryOpen, onActivityClick, hideActivitySummaryCaret = false }) {
  const [displayedMessage, setDisplayedMessage] = useState('');
  const [feedback, setFeedback] = useState({});
  const { accessToken } = useContext(AuthContext);
  const [hasImageError, setHasImageError] = useState(false);
  const { t: tChat, i18n: i18nChat } = useTranslation('chat');

  // Texto de sistema que o backend injeta na própria resposta (bloco
  // "Arquivos gerados", avisos de skill, frases do fluxo de edição) chega
  // embrulhado num marcador de tradução - ver utils/translateInlineText.js.
  // Traduzido AQUI, antes do efeito de digitação abaixo: se o marcador
  // entrasse no texto digitado, ele seria revelado caractere a caractere e
  // apareceria cru na tela enquanto o comentário não fechasse.
  const conteudoTraduzido = useMemo(() => {
    const limpo = removerMarcadoresInternos(translateInlineText(msg.content));
    return msg.role === 'user' ? limpo : normalizarSeparadorMilhar(limpo, i18nChat.language);
  }, [msg.content, msg.role, i18nChat.language]);

  useEffect(() => {
    if (msg.feedback) {
      setFeedback(prev => ({ ...prev, [index]: msg.feedback }));
    }
  }, [msg.feedback, index]);

  // Efeito de digitação — caracteres em lote (não 1 a 1) para reduzir a frequência
  // de re-render do DOM durante o streaming: menos chances de colisão com extensões
  // que também mutam a árvore (ex.: Google Tradutor), mantendo a velocidade percebida.
  //
  // So' vale para a resposta que ESTA aba esta gerando: mensagem vinda do
  // historico traz `status` (backend/chat/views.py::_build_assistant_message),
  // a montada ao vivo pelo SSE nao. Sem esse filtro, voltar para um chat que
  // ficou executando re-digitava do zero um texto que o servidor ja' devolveu
  // pronto - `isSending` e' do componente, nao da sessao, e continua ligado
  // enquanto a geracao abandonada nao fecha.
  const veioDoHistorico = Boolean(msg.status);

  useEffect(() => {
    let intervalID;

    if (msg.role === 'assistant' && isSending && !veioDoHistorico) {
      let fullText = conteudoTraduzido || '';
      let currentText = '';
      let i = 0;

      intervalID = setInterval(() => {
        if (i < fullText.length) {
          currentText += fullText[i];
          setDisplayedMessage(currentText);
          i++;
        } else {
          clearInterval(intervalID);
        }
      }, 10);

      return () => clearInterval(intervalID);
    } else {
      setDisplayedMessage(conteudoTraduzido);
    }

    return () => clearInterval(intervalID);
  }, [conteudoTraduzido, isSending, msg.role, veioDoHistorico]);

  const sendFeedback = async (response, feedbackType, index) => {
    setFeedback(prevFeedback => ({ ...prevFeedback, [index]: feedbackType }));
    const interactionId = msg._id;

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/post_feedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          'feedback': feedbackType,
          'interaction_id': interactionId
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        console.error("Erro do servidor:", data.message || "Erro desconhecido");
      }
    } catch (error) {
      console.error('Erro ao enviar feedback:', error);
    }
  };

  const copyToClipboard = (content) => {
    navigator.clipboard.writeText(content).catch(err => {
      console.error('Falha ao copiar o texto:', err);
    });
  };


  if (msg.type === 'n8n_confirmation') {
    return (
      <div className="message-wrapper message-wrapper--assistant">
        <N8nConfirmationCard pending={msg.n8n_pending} />
      </div>
    );
  }

  // Enquanto não há nenhum texto ainda (só o indicador de atividade), a mensagem é só
  // o indicador - sem wrapper de markdown vazio por baixo.
  if (msg.role === 'assistant' && msg.activity_step && !msg.activity_summary && !msg.content) {
    return (
      <div className="Message-message-wrapper Message-message-wrapper--assistant">
        <div className="Message-message Message-message--assistant">
          <ActivityStepInline step={msg.activity_step} onClick={onActivityClick} executionStartedAt={msg.activity_started_at} hideDuration={isActivityHistoryOpen} />
        </div>
      </div>
    );
  }

  if (msg.type === 'skill_created') {
    return (
      <div className="Message-message-wrapper Message-message-wrapper--assistant">
        <div className="Message-message Message-message--assistant">
          {msg.content && (
            <div className="Message-markdown-body notranslate">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{conteudoTraduzido}</ReactMarkdown>
            </div>
          )}
          <SkillResultCard skill={msg.skill_created} />
        </div>
      </div>
    );
  }

  const references = msg.links || msg.references || links || [];

  return (
    <div className={`Message-message-wrapper ${isReportBox ? 'Message-message-wrapper--center' : `Message-message-wrapper--${msg.role}`}`}>
      {msg.role !== 'system' && (
        <div className={`Message-message Message-message--${msg.role}`}>
          {msg.role === 'assistant' && msg.activity_step && !msg.activity_summary && (
            // Execução ainda em andamento (activity_summary só é gravado quando o
            // streaming termina de verdade - ver UnifiedChat.js) - mostra o indicador
            // ao vivo (etapa atual + cronômetro correndo) em cima do texto que já foi
            // narrado, em vez de esconder qualquer sinal de atividade até o fim.
            <ActivityStepInline step={msg.activity_step} onClick={onActivityClick} executionStartedAt={msg.activity_started_at} hideDuration={isActivityHistoryOpen} />
          )}
          {msg.role === 'assistant' && msg.activity_summary && !isActivityHistoryOpen
            // Sem painel pra abrir (prévia do assistente ou tela <1024px -
            // hideActivitySummaryCaret), o pill genérico ("Ver atividade", sem
            // skill_name) não carrega nenhuma informação útil - só existiria
            // como convite pra abrir algo que não existe ali. O pill com nome
            // de skill ("Acionou skill X") continua aparecendo, porque esse
            // sim informa algo.
            && !(hideActivitySummaryCaret && !msg.activity_summary.skill_name) && (
            <ActivityStepSummary
              summary={msg.activity_summary}
              onClick={hideActivitySummaryCaret ? undefined : onActivityClick}
              hideCaret={hideActivitySummaryCaret}
            />
          )}
          {isSending && displayedMessage === '' ? (
            <div className="Message-wave-animation">
              <span className="Message-dot"></span>
              <span className="Message-dot"></span>
              <span className="Message-dot"></span>
            </div>
          ) : (
            <>
              {/* Renderização principal do texto usando ReactMarkdown */}
              {Array.isArray(displayedMessage) ? (
                <>
                  {displayedMessage.filter(i => i.type === 'text').map((t, idx) => {
                    const skillCommandText = msg.role === 'user' ? msg.skillCommandText : null;
                    const skillCommandIndex = skillCommandText ? t.text.indexOf(skillCommandText) : -1;

                    if (skillCommandIndex === -1) {
                      return (
                        <div className="Message-markdown-body notranslate" key={idx}>
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                              table: ({node, ...props}) => (
                                <div className="Message-chat-table-wrapper">
                                  <table className="Message-chat-table" {...props} />
                                </div>
                              ),
                              a: ({node, ...props}) => (
                                <a {...props} target="_blank" rel="noopener noreferrer" />
                              )
                            }}
                          >
                            {linkifyReferences(t.text, references)}
                          </ReactMarkdown>
                        </div>
                      );
                    }

                    const before = t.text.slice(0, skillCommandIndex);
                    const afterRaw = t.text.slice(skillCommandIndex + skillCommandText.length);
                    const afterLeadingSpace = (afterRaw.match(/^\s+/) || [''])[0];
                    const after = afterRaw.slice(afterLeadingSpace.length);

                    return (
                      <div className="Message-markdown-body" key={idx}>
                        {before}
                        <span className="Message-skill-command-highlight">{skillCommandText}</span>
                        {afterLeadingSpace}
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            p: ({node, ...props}) => <>{props.children}</>,
                            table: ({node, ...props}) => (
                              <div className="Message-chat-table-wrapper">
                                <table className="Message-chat-table" {...props} />
                              </div>
                            ),
                            a: ({node, ...props}) => (
                              <a {...props} target="_blank" rel="noopener noreferrer" />
                            )
                          }}
                        >
                          {linkifyReferences(after, references)}
                        </ReactMarkdown>
                      </div>
                    );
                  })}
                  {displayedMessage.filter(i => i.type === 'image_url').map((img, idx) => (
                    !hasImageError && (
                      <img
                        key={`img-${idx}`}
                        src={img.image_url.url}
                        alt={tChat("messageImage")}
                        onError={() => setHasImageError(true)}
                        style={{
                          maxWidth: '100%', maxHeight: '150px', borderRadius: '8px',
                          objectFit: 'contain', marginRight: '5px', marginBottom: '5px'
                        }}
                      />
                    )
                  ))}
                  {hasImageError && (
                    <p style={{ fontStyle: 'italic', fontSize: '11px', color: '#888', marginTop: '0.25rem' }}>
                      {tChat('img_erro')}
                    </p>
                  )}
                </>
              ) : !displayedMessage && msg.error ? (
                // Tarefa 5.2 (specs/front_planning_tools_ajustes_finais/plan.md): skill via "/"
                // que falha no meio persiste com `results` vazio e a mensagem de erro num campo
                // à parte (`msg.error`, backend/chat/views.py::_build_assistant_message) - sem
                // isso, a bolha ficava vazia e muda no reload, sem nenhuma explicação.
                <div className="Message-markdown-body notranslate" style={{ color: '#d32f2f' }}>
                  {translateBackendError(msg.error)}
                </div>
              ) : (
                <div className="Message-markdown-body notranslate">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      // Interceptamos a tabela para criar um wrapper responsivo
                      table: ({node, ...props}) => (
                        <div className="Message-chat-table-wrapper">
                          <table className="Message-chat-table" {...props} />
                        </div>
                      ),
                      a: ({node, ...props}) => (
                        <a {...props} target="_blank" rel="noopener noreferrer" />
                      )
                    }}
                  >
                    {linkifyReferences(displayedMessage, references)}
                  </ReactMarkdown>
                </div>
              )}

              {/* Renderização de Arquivos Anexos (Mantida intacta) */}
              {msg.files && msg.files.length > 0 && (
                <>
                  {msg.files.some(f => f?.status === "indisponivel") ? (
                    <p style={{ fontStyle: 'italic', fontSize: '12px', color: '#888', marginTop: '0.5rem' }}>
                      {tChat('file_unavailable')}
                    </p>
                  ) : (
                    <>
                      <div className="Message-image-preview-container-message">
                        {msg.files.filter(file => file.type?.startsWith("image/") || file.preview).map((file, index) => (
                          <img
                            key={`preview-${index}`}
                            src={file.preview || file.url}
                            alt={file.name}
                            style={{ maxWidth: '200px', borderRadius: '8px', margin: '5px 5px' }}
                          />
                        ))}
                      </div>
                      <div className="Message-file-preview-container-message">
                        {msg.files.filter(file => !(file.type?.startsWith("image/") || file.preview)).map((file, index) => (
                          <UploadButtonMessage
                            key={`upload-${index}`}
                            filename={file.name}
                            onDelete={null}
                            index={index}
                            width="100%"
                          />
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}

              {/* Controles de Feedback */}
              {msg.role === 'assistant' && index > 1 && msg.content !== '' && (
                <div className="Message-feedback-container">
                  <button data-testid="like-icon" className="Message-feedback-button" onClick={() => sendFeedback(msg.content, 'positive', index)}>
                    <img src={feedback[index] === 'positive' ? "/imagens/like_clicado.svg" : "/imagens/like_nao_clicado.svg"} alt={tChat("likeIcon")} className="Message-feedback-icon" />
                  </button>
                  <button data-testid="dislike-icon" className="Message-feedback-button" onClick={() => sendFeedback(msg.content, 'negative', index)}>
                    <img src={feedback[index] === 'negative' ? "/imagens/deslike_clicado.svg" : "/imagens/deslike_nao_clicado.svg"} alt={tChat("dislikeIcon")} className="Message-feedback-icon" />
                  </button>
                  <button data-testid="copy-icon" className="Message-feedback-button" onClick={() => copyToClipboard(conteudoTraduzido)}>
                    <img src="/imagens/copy.svg" alt={tChat("copyIcon")} className="Message-feedback-icon" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default Message;